import React, { useState, useEffect, useRef } from 'react';
import { settingsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Quote, Star, Plus, Edit2, Trash2, Eye, EyeOff, Upload, X, User } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const EMPTY_FORM = {
  author_name: '',
  author_role: '',
  content: '',
  image_url: '',
  score: '',
  rating: 5,
  is_active: true,
  sort_order: 0,
};

export function AdminTestimonialsManager() {
  const { user } = useAuth();
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    if (user?.id) loadTestimonials();
  }, [user?.id]);

  const loadTestimonials = async () => {
    setLoading(true);
    try {
      const data = await settingsAPI.getCenterTestimonials(user.id);
      setTestimonials(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM, sort_order: testimonials.length });
    setShowModal(true);
  };

  const openEdit = (t) => {
    setEditing(t.id);
    setForm({
      author_name: t.author_name || '',
      author_role: t.author_role || '',
      content: t.content || '',
      image_url: t.image_url || '',
      score: t.score || '',
      rating: t.rating ?? 5,
      is_active: !!t.is_active,
      sort_order: t.sort_order ?? 0,
    });
    setShowModal(true);
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await settingsAPI.uploadTestimonialPhoto(file);
      if (res?.url) setForm(f => ({ ...f, image_url: res.url }));
      else toast.error('Erreur upload photo');
    } catch {
      toast.error('Erreur upload photo');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!form.author_name.trim() || !form.content.trim()) {
      toast.error('Nom et témoignage sont requis');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        rating: parseInt(form.rating, 10) || 5,
        sort_order: parseInt(form.sort_order, 10) || 0,
        is_active: form.is_active ? 1 : 0,
        image_url: form.image_url || null,
        score: form.score || null,
      };
      if (editing) {
        await settingsAPI.updateTestimonial(user.id, editing, payload);
        toast.success('Témoignage mis à jour');
      } else {
        await settingsAPI.createTestimonial(user.id, payload);
        toast.success('Témoignage ajouté');
      }
      setShowModal(false);
      loadTestimonials();
    } catch {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (t) => {
    try {
      await settingsAPI.updateTestimonial(user.id, t.id, { is_active: t.is_active ? 0 : 1 });
      setTestimonials(prev => prev.map(x => x.id === t.id ? { ...x, is_active: x.is_active ? 0 : 1 } : x));
    } catch {
      toast.error('Erreur');
    }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Supprimer le témoignage de "${t.author_name}" ?`)) return;
    try {
      await settingsAPI.deleteTestimonial(user.id, t.id);
      toast.success('Supprimé');
      setTestimonials(prev => prev.filter(x => x.id !== t.id));
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const StarRating = ({ value, onChange }) => (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(n => (
        <button key={n} type="button" onClick={() => onChange && onChange(n)}>
          <Star className={`h-5 w-5 transition-colors ${n <= value ? 'fill-yellow-400 text-yellow-400' : 'text-gray-400'}`} />
        </button>
      ))}
    </div>
  );

  const Avatar = ({ t, size = 'md' }) => {
    const s = size === 'sm' ? 'w-10 h-10 text-base' : 'w-16 h-16 text-2xl';
    const imgSrc = t.image_url ? (t.image_url.startsWith('http') ? t.image_url : `${API_URL}${t.image_url}`) : null;
    return imgSrc ? (
      <img src={imgSrc} alt={t.author_name} className={`${s} rounded-full object-cover ring-2 ring-primary/20`} />
    ) : (
      <div className={`${s} rounded-full bg-gradient-to-br from-primary/30 to-primary/10 flex items-center justify-center font-bold text-primary ring-2 ring-primary/20`}>
        {t.author_name?.[0]?.toUpperCase() || <User className="h-5 w-5" />}
      </div>
    );
  };

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Quote className="h-6 w-6 text-primary" />
            Témoignages étudiants
          </h2>
          <p className="text-muted-foreground mt-1">
            Ces témoignages s'affichent sur votre page d'inscription
          </p>
        </div>
        <button
          onClick={openAdd}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-all shadow-lg"
        >
          <Plus className="h-4 w-4" />
          Ajouter
        </button>
      </div>

      {/* Empty state */}
      {testimonials.length === 0 && (
        <div className="bg-card rounded-2xl border border-dashed p-16 text-center">
          <Quote className="h-12 w-12 text-muted-foreground/40 mx-auto mb-4" />
          <p className="text-lg font-semibold text-muted-foreground">Aucun témoignage</p>
          <p className="text-sm text-muted-foreground mt-1 mb-6">Ajoutez vos premiers témoignages d'étudiants</p>
          <button onClick={openAdd} className="px-6 py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-all">
            Ajouter un témoignage
          </button>
        </div>
      )}

      {/* Testimonials grid */}
      {testimonials.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {testimonials.map(t => (
            <div key={t.id} className={`bg-card rounded-2xl border p-5 transition-all ${!t.is_active ? 'opacity-50' : 'shadow-sm hover:shadow-md'}`}>
              {/* Stars + actions */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex gap-0.5">
                  {[1,2,3,4,5].map(n => (
                    <Star key={n} className={`h-3.5 w-3.5 ${n <= (t.rating || 5) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                  ))}
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => handleToggle(t)} title={t.is_active ? 'Masquer' : 'Afficher'}
                    className="p-1.5 rounded-lg hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground">
                    {t.is_active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </button>
                  <button onClick={() => openEdit(t)} title="Modifier"
                    className="p-1.5 rounded-lg hover:bg-secondary transition-colors text-muted-foreground hover:text-primary">
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button onClick={() => handleDelete(t)} title="Supprimer"
                    className="p-1.5 rounded-lg hover:bg-destructive/10 transition-colors text-muted-foreground hover:text-destructive">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Quote */}
              <p className="text-sm text-muted-foreground leading-relaxed line-clamp-4 mb-4 italic">
                "{t.content}"
              </p>

              {/* Author */}
              <div className="flex items-center gap-3 pt-3 border-t">
                <Avatar t={t} size="sm" />
                <div className="min-w-0">
                  <p className="font-semibold text-sm truncate">{t.author_name}</p>
                  {t.author_role && <p className="text-xs text-muted-foreground truncate">{t.author_role}</p>}
                  {t.score && (
                    <span className="inline-block text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-full mt-1">
                      Score : {t.score}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-card rounded-2xl border shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            {/* Modal header */}
            <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-card z-10">
              <h3 className="text-lg font-bold">{editing ? 'Modifier le témoignage' : 'Nouveau témoignage'}</h3>
              <button onClick={() => setShowModal(false)} className="p-2 rounded-lg hover:bg-secondary transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Photo upload */}
              <div>
                <label className="block text-sm font-medium mb-2">Photo (optionnel)</label>
                <div className="flex items-center gap-4">
                  {form.image_url ? (
                    <img src={form.image_url.startsWith('http') ? form.image_url : `${API_URL}${form.image_url}`}
                      alt="preview" className="w-16 h-16 rounded-full object-cover ring-2 ring-primary/30" />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                      <User className="h-7 w-7 text-muted-foreground" />
                    </div>
                  )}
                  <div className="flex-1 space-y-2">
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handlePhotoUpload}
                    />
                    <button type="button" onClick={() => fileRef.current?.click()}
                      disabled={uploading}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg border text-sm hover:bg-secondary transition-colors disabled:opacity-50">
                      <Upload className="h-4 w-4" />
                      {uploading ? 'Upload...' : 'Choisir une photo'}
                    </button>
                    <input
                      type="text"
                      placeholder="Ou coller une URL d'image"
                      value={form.image_url}
                      onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))}
                      className="w-full h-9 px-3 rounded-lg border bg-background text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Author name */}
              <div>
                <label className="block text-sm font-medium mb-1.5">Nom <span className="text-destructive">*</span></label>
                <input
                  type="text"
                  placeholder="Marie Dupont"
                  value={form.author_name}
                  onChange={e => setForm(f => ({ ...f, author_name: e.target.value }))}
                  className="w-full h-10 px-3 rounded-lg border bg-background text-sm"
                />
              </div>

              {/* Role */}
              <div>
                <label className="block text-sm font-medium mb-1.5">Rôle / Description</label>
                <input
                  type="text"
                  placeholder="Étudiante TCF Canada — Paris"
                  value={form.author_role}
                  onChange={e => setForm(f => ({ ...f, author_role: e.target.value }))}
                  className="w-full h-10 px-3 rounded-lg border bg-background text-sm"
                />
              </div>

              {/* Score */}
              <div>
                <label className="block text-sm font-medium mb-1.5">Score obtenu (optionnel)</label>
                <input
                  type="text"
                  placeholder="ex: 490/699 • Niveau B2"
                  value={form.score}
                  onChange={e => setForm(f => ({ ...f, score: e.target.value }))}
                  className="w-full h-10 px-3 rounded-lg border bg-background text-sm"
                />
              </div>

              {/* Content */}
              <div>
                <label className="block text-sm font-medium mb-1.5">Témoignage <span className="text-destructive">*</span></label>
                <textarea
                  rows={4}
                  placeholder="Grâce à cette plateforme, j'ai pu préparer mon TCF Canada efficacement..."
                  value={form.content}
                  onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
                  className="w-full px-3 py-2.5 rounded-lg border bg-background text-sm resize-none"
                />
              </div>

              {/* Rating */}
              <div>
                <label className="block text-sm font-medium mb-2">Note</label>
                <StarRating value={form.rating} onChange={v => setForm(f => ({ ...f, rating: v }))} />
              </div>

              {/* Active toggle */}
              <div className="flex items-center justify-between p-3 rounded-xl bg-muted/50 border">
                <div>
                  <p className="text-sm font-medium">Afficher sur la page</p>
                  <p className="text-xs text-muted-foreground">Visible par les visiteurs</p>
                </div>
                <button
                  type="button"
                  onClick={() => setForm(f => ({ ...f, is_active: !f.is_active }))}
                  className={`relative w-11 h-6 rounded-full transition-colors ${form.is_active ? 'bg-primary' : 'bg-muted-foreground/30'}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.is_active ? 'translate-x-5' : 'translate-x-0'}`} />
                </button>
              </div>
            </div>

            {/* Modal footer */}
            <div className="flex gap-3 p-6 pt-0">
              <button onClick={() => setShowModal(false)}
                className="flex-1 h-11 rounded-xl border font-medium hover:bg-secondary transition-colors">
                Annuler
              </button>
              <button onClick={handleSave} disabled={saving}
                className="flex-1 h-11 rounded-xl bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50">
                {saving ? 'Sauvegarde...' : editing ? 'Mettre à jour' : 'Ajouter'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
